import { Component } from '@theme/component';
import { mediaQueryLarge, requestIdleCallback } from '@theme/utilities';

/**
 * Hosts the country / language selector as a full-page modal
 * (snippets/country-selector-overlay.liquid).
 *
 * Entry points across the theme are plain anchors to the selector page. This component intercepts
 * those clicks, fetches that page's selector section through the Section Rendering API, injects it
 * and opens the drawer — so the shopper keeps their place instead of navigating away. If anything
 * here fails or has not loaded yet, the anchor is left alone and the browser navigates, which is
 * also what crawlers and no-JS visitors get.
 *
 * The selector page is fetched and its section lifted out of the response, rather than asking the
 * Section Rendering API for the section on its own. Section rendering addresses a section by file
 * name, which renders it standalone with schema *defaults* — so anything the merchant configured on
 * the section (its heading, its brand image) would appear on the page and silently not in the modal.
 * A JSON template's configured instance is only addressable through the full generated section id,
 * which nothing client-side can know. Fetching the page is the only way both surfaces show the same
 * thing while the settings stay on the section where a merchant expects to find them.
 *
 * If that page is missing — it has to be created and given the template — the fetch falls back to
 * rendering the section standalone by file name against the current URL. That path loses the
 * merchant's section settings, since a standalone render only gets schema defaults, but it means
 * the modal works on a store that has not been provisioned yet instead of failing outright.
 *
 * Either way the section arrives rendered in another page's context, so its localization form
 * carries the wrong `return_to`; it is rewritten to the current URL below.
 *
 * Injected `<script>` tags never execute, so the selector's own component module is imported
 * explicitly — lazily, on first open, to keep it off pages nobody opens it from.
 *
 * Note this deliberately does not use `ref=`: the framework scopes ref collection to the nearest
 * component, and everything here lives on or inside <theme-drawer>, which is a component in its own
 * right — so those refs land on the drawer, not here. Scoped queries instead.
 */
class CountrySelectorOverlay extends Component {
  /** @type {Promise<void> | null} In-flight or settled load; the section is fetched at most once. */
  #loaded = null;

  /**
   * The warmed section, parsed and already imported into this document, waiting to be inserted.
   * @type {Element | null}
   */
  #section = null;

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#warmWhenIdle();

    // Reset on the dialog's own `close` event, not on `theme-drawer:close`. The drawer dispatches
    // that one at the *start* of its close, so resetting there would play the whole slide and
    // cross-fade backwards on the way out. The native event fires after `panel.close()`, when
    // nothing is rendered, so the panel is back to step one invisibly and reopening shows no
    // leftover animation.
    const dialog = this.querySelector('dialog');
    dialog?.addEventListener('close', this.#reset, { signal });

    // On desktop the dialog spans the viewport so the panel has somewhere to slide into, which
    // means the drawer's own outside-click close never fires — every click is technically inside
    // it. Clicking the image area reads as dismissing, so treat it that way.
    dialog?.addEventListener('click', this.#onOutsidePanelClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
  }

  /**
   * Warm the selector once the page has finished its own critical work.
   *
   * This deliberately does NOT inject. Injecting early would put the selector's ~700 nodes — and
   * its 237 country anchors — into every page's DOM, which is the exact cost the whole
   * page-plus-fetch architecture exists to avoid, and it would put those anchors back on every URL
   * for a rendering crawler. Instead the section is parsed and held off-document, and its images —
   * the brand image and the logo over it — are downloaded and decoded, so opening is a DOM
   * insertion with no fetch, no parse and no decode left to pay for.
   *
   * Skipped on metered or very slow connections, where speculatively downloading a page and a
   * full-bleed image is a poor trade.
   */
  #warmWhenIdle() {
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData) return;
    if (/(^|-)2g$/.test(connection?.effectiveType ?? '')) return;

    // Bounded: an unbounded idle callback can be deferred indefinitely on a busy main thread, and
    // until it runs the panel still opens onto the background colour rather than the image. Two
    // seconds keeps it out of the page's own critical work while making the wait predictable.
    const warm = () => requestIdleCallback(() => this.#warm(), { timeout: 2000 });

    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true, signal: this.#controller?.signal });
  }

  /** @returns {Promise<void>} */
  async #warm() {
    // No entry point means no selector to prepare.
    if (!this.#triggerHref()) return;

    try {
      // The same request opening makes, so the warm-up prepares exactly what the open would
      // have fetched.
      const section = await this.#standalone();
      if (!section) return;

      // Imported into this document now, so the later insertion is a plain move rather than
      // another parse. Every entry point resolves to the same selector URL — they differ only in
      // the fragment, which is never sent to the server — so one warmed copy serves all of them.
      this.#section = document.importNode(section, true);

      // decode(), not merely a download. Fetching the bytes leaves the decode to be paid at
      // insertion, which is the one moment it is guaranteed to be visible. Awaited on the elements
      // that will actually be inserted, so the work is not repeated for throwaway copies.
      //
      // Every image the section carries, not just the brand image: the logo sits on top of it and
      // is the section's own `media_logo` setting, so the loading skeleton cannot show it. If only
      // the brand image were decoded the logo would pop in a beat after it. A decode that fails —
      // a missing or broken asset — must not sink the rest, hence the per-image catch.
      const images = /** @type {HTMLImageElement[]} */ ([...this.#section.querySelectorAll('img')]);
      await Promise.all(images.map((image) => image.decode().catch(() => {})));
    } catch {
      // A warm-up failing is not worth surfacing; the click path reports properly.
    }
  }

  /** @returns {string | null} The selector page URL, taken from any entry point on the page. */
  #triggerHref() {
    const trigger = document.querySelector('a[aria-controls="country-selector"]');
    return trigger instanceof HTMLAnchorElement ? trigger.href : null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onOutsidePanelClick = (event) => {
    // Mobile has no image beside the panel — the panel is the whole drawer, so there is nothing
    // to click next to it and any hit would be a mis-close.
    if (!mediaQueryLarge.matches) return;
    if (event.defaultPrevented || event.button !== 0) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (target?.closest?.('.country-selector__panel-stack, .theme-drawer__close-button')) return;

    this.#drawer?.close();
  };

  /**
   * Put the selector back to how it was first rendered: step one, the first region expanded, and
   * both the panel and the country list scrolled to the top.
   */
  #reset = () => {
    const body = this.#body;
    const root = body?.querySelector('country-selector-component');
    if (!body || !(root instanceof HTMLElement)) return;

    // Set the attribute rather than calling the component's showOverview(), which moves focus —
    // the drawer has just handed focus back to the trigger and must keep it.
    root.dataset.activeStep = 'overview';

    const regions = body.querySelectorAll('.country-selector__region');
    regions.forEach((region, index) => region.toggleAttribute('open', index === 0));

    body.scrollTop = 0;
    for (const list of body.querySelectorAll('.country-selector__countries')) list.scrollTop = 0;
  };

  /**
   * Open the overlay. Bound from the entry-point anchors via `on:click`.
   *
   * @param {MouseEvent} event
   */
  async open(event) {
    // Anything but a plain left click keeps the anchor's own behaviour, so modifier-clicking the
    // entry point still opens the selector page in a new tab.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('a[href]');
    if (!(trigger instanceof HTMLAnchorElement)) return;

    // On the selector page itself the section is already on screen. Opening a modal copy would put
    // a second set of the same element ids in the document and break the :target fallback, so let
    // the link behave like a link. The copy this overlay injected does not count — once it is
    // cached, every later click would otherwise fall through to a navigation.
    const onPage = document.querySelector('country-selector-component');
    if (onPage && !this.contains(onPage)) return;

    event.preventDefault();

    // theme-drawer.js is a sibling module with no guaranteed load order relative to this one.
    // Without waiting, an early click finds an un-upgraded element with no open() and silently
    // does nothing.
    await customElements.whenDefined('theme-drawer');
    this.#drawer?.open();

    try {
      await this.#load();
      this.#applyStep(trigger);
    } catch (error) {
      // The panel is already open with the status line still in place; turn it into an error that
      // offers the plain link, rather than leaving a spinner that never resolves.
      this.#fail(trigger.href);
      console.error('country-selector-overlay: failed to load the selector', error);
    }
  }

  /**
   * Safe to use unguarded: every caller runs after `customElements.whenDefined('theme-drawer')`
   * has resolved in `open()`, and defining an element upgrades the instances already in the
   * document, so it is upgraded by the time anything asks it to open or close.
   *
   * @returns {{ open: () => void, close: () => void } | null}
   */
  get #drawer() {
    return /** @type {any} */ (this.querySelector('theme-drawer'));
  }

  /**
   * Injection target for the fetched section. Ships holding the loading skeleton and marked
   * `aria-busy`; both go the moment something real replaces them, whether that is the section or
   * the error fallback.
   *
   * @returns {HTMLElement | null}
   */
  get #body() {
    return this.querySelector('.country-selector-overlay__body');
  }

  /**
   * Fetch, inject and hydrate the selector once.
   *
   * @returns {Promise<void>}
   */
  #load() {
    if (this.#loaded) return this.#loaded;

    this.#loaded = (async () => {
      const body = this.#body;
      if (!body) throw new Error('country-selector-overlay: missing injection target');

      // When the warm-up has already run, nothing here is awaited before the insertion, so it
      // lands in the same task as the click — before the browser gets a chance to paint. That is
      // what skips the skeleton outright instead of flashing it for a frame. Only hydration, which
      // is invisible, is left to await.
      let section = this.#section;
      this.#section = null;

      if (!section) {
        const fetched = await this.#standalone();
        if (!fetched) throw new Error('country-selector-overlay: could not render the selector');
        section = document.importNode(fetched, true);
      }

      body.replaceChildren(section);
      body.removeAttribute('aria-busy');
      this.#syncReturnTo();

      // Registers <country-selector-component>, which upgrades the markup just injected.
      await import('@theme/country-selector');
    })();

    // A failed load must not be cached, or the overlay stays broken for the rest of the session.
    this.#loaded.catch(() => {
      this.#loaded = null;
    });

    return this.#loaded;
  }

  /**
   * The selector section, rendered on its own against whatever URL the shopper is on.
   *
   * This used to be the fallback, behind a fetch of the selector page itself — the configured
   * instance, which was the only way to get the brand image, since an `image_picker` cannot carry
   * a schema default. That page is ~2.1 MB of HTML, 136 KB over the wire, and it was fetched
   * speculatively on every page load to use 40 KB of it. The image now falls back to Theme
   * settings (sections/country-selector.liquid), so the section renders complete on its own and
   * the page fetch has nothing left to offer: the same markup for 3.9 KB.
   *
   * Works before the selector page has been created, too, which the fallback was originally for.
   *
   * @returns {Promise<Element | null>}
   */
  async #standalone() {
    const url = new URL(document.location.href);
    url.searchParams.set('section_id', 'country-selector');

    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) return null;

    const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const selector = fragment.querySelector('country-selector-component');
    return selector?.closest('.shopify-section') ?? selector;
  }

  /**
   * Show the step the trigger asked for. The step travels as the href fragment, which is the same
   * value the no-JS `:target` fallback uses, so one attribute drives both paths. Always applied,
   * including the default, so a reopen does not inherit whichever step the last visit left behind.
   *
   * @param {HTMLAnchorElement} trigger
   */
  #applyStep(trigger) {
    const body = this.#body;
    const root = body?.querySelector('country-selector-component');
    if (!root) return;

    customElements.upgrade(root);

    // Step ids are `country-selector-<step>`, matching the component's own step names.
    const hash = new URL(trigger.href, document.location.href).hash;
    const wanted = hash && body?.querySelector(hash) ? hash.slice(1).replace('country-selector-', '') : 'overview';

    // Already there — usually the case now that closing resets the panel — so leave focus alone.
    if ((/** @type {HTMLElement} */ (root).dataset.activeStep ?? 'overview') === wanted) return;

    // Prefer the component's own methods so focus moves into the step that just appeared.
    const show = /** @type {any} */ (root)[wanted === 'regions' ? 'showRegions' : 'showOverview'];
    if (typeof show === 'function') show.call(root);
    else /** @type {HTMLElement} */ (root).dataset.activeStep = wanted;
  }

  /** Point the localization form back at the page the shopper is on, not the selector page. */
  #syncReturnTo() {
    const input = this.#body?.querySelector('input[name="return_to"]');
    if (input instanceof HTMLInputElement) {
      input.value = `${document.location.pathname}${document.location.search}`;
    }
  }

  /**
   * Replace the skeleton with something the shopper can act on. The loading announcement is
   * visually hidden, so an error has to render its own visible element.
   *
   * @param {string} href
   */
  #fail(href) {
    const body = this.#body;
    if (!body) return;

    const message = document.createElement('p');
    message.className = 'country-selector-overlay__error';
    message.setAttribute('role', 'status');

    const link = document.createElement('a');
    link.href = href;
    link.textContent = this.dataset.errorLabel ?? href;

    message.append(link);
    body.replaceChildren(message);
    body.removeAttribute('aria-busy');
  }
}

if (!customElements.get('country-selector-overlay')) {
  customElements.define('country-selector-overlay', CountrySelectorOverlay);
}
